import { getCookie } from "cookies-next";
import urlTemplate from "url-template";
import { observeEvent } from "~/analytics";

// From: https://stackoverflow.com/questions/41102060/typescript-extending-error-class
export class ErrorWithCode extends Error {
  code?: string;
  constructor(message: string, code: string) {
    super(message); // 'Error' breaks prototype chain here
    this.code = code;
    Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
  }
}

type EmptyObject = Record<string, unknown>;

export class AppAPIRestClient {
  private baseURL: string;
  private appKey: string;

  constructor(baseURL: string, appKey: string) {
    this.baseURL = baseURL;
    this.appKey = appKey;
  }

  login = this.POST<EmptyObject, LoginPayload, LoginResult>("/login", "login");
  serverSideLogin = this.POST<EmptyObject, LoginPayload, LoginResult>("/login");

  linkLoginStart = this.POST<EmptyObject, LoginLinkPayload, LoginLinkResult>(
    "/login/link",
  );
  linkLoginCode = this.POST<EmptyObject, LoginCodePayload, LoginCodeResult>(
    "/login/code",
  );

  updateProfile = this.POST<EmptyObject, UpdateProfilePayload, LoginResult>(
    "/customers/me",
    "update_profile",
  );

  signup = this.POST<EmptyObject, SignupPayload, SignupResult>(
    "/signup",
    "sign_up",
  );

  logout = this.DELETE<EmptyObject, EmptyObject>("/sessions/current");

  sendPasswordReset = this.POST<
    EmptyObject,
    SendPasswordResetPayload,
    EmptyObject
  >("/recovery/reset", "send_password_reset");

  resetPassword = this.POST<
    { code: string },
    ResetPasswordPayload,
    EmptyObject
  >("/recovery/reset/{code}");

  verifyEmail = this.POST<EmptyObject, VerifyEmailPayload, EmptyObject>(
    "/session/verify",
    "verify_email_jwt",
  );

  sendVerifyEmail = this.POST<EmptyObject, EmptyObject, EmptyObject>(
    "/session/verify/send",
    "send_verify_email",
  );

  sendSupportMessage = this.POST<
    EmptyObject,
    SendSupportMessagePayload,
    EmptyObject
  >("/comment", "send_support_message");

  async sendMetrics(metrics: string) {
    await fetch(this.baseURL + "/metrics", {
      method: "POST",
      body: metrics,
      headers: {
        "Content-Type": "text/plain",
      },
    });
  }

  private async doRequest<P, R>(
    method: string,
    path: string,
    payload?: P,
  ): Promise<R> {
    const token = getCookie("token");

    const res = await fetch(this.baseURL + path, {
      method: method,
      body: JSON.stringify(payload),
      headers: {
        authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        "Tidy-App-Key": this.appKey,
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        window.location.href = "/logout";
      }

      let errBody;
      try {
        errBody = await res.json();
      } catch (err) {
        console.error("error parsing error message", err);
        throw new Error("Unknown error");
      }

      if (errBody.ErrorMessage && errBody.ErrorCode) {
        throw new ErrorWithCode(errBody.ErrorMessage, errBody.ErrorCode);
      }

      throw new Error(errBody.message || errBody.ErrorMessage);
    }

    return res.json();
  }

  // private GET<Params, Result>(pathTemplate: string) {
  //   const t = urlTemplate.parse(pathTemplate);

  //   return (params: Params): Promise<Result> =>
  //     this.doRequest("GET", t.expand(params));
  // }

  private DELETE<Params, Result>(pathTemplate: string) {
    const t = urlTemplate.parse(pathTemplate);

    return (params: Params): Promise<Result> =>
      this.doRequest("DELETE", t.expand(params));
  }

  private POST<Params, Payload, Result>(
    pathTemplate: string,
    eventAction?: string,
  ) {
    const t = urlTemplate.parse(pathTemplate);

    return async (params: Params, payload: Payload): Promise<Result> => {
      const result: Result = await this.doRequest(
        "POST",
        t.expand(params),
        payload,
      );

      if (eventAction) {
        observeEvent({ action: eventAction });
      }

      return result;
    };
  }
}

type LoginPayload = {
  LoginService: string;
  DeviceID: string;
  FirstName?: string;
  LastName?: string;
} & (
  | {
      EmailAddress: string;
      Password: string;
      Signature: string;
    }
  | {
      AppleIdToken: string;
    }
  | {
      GoogleToken: string;
    }
  | {
      Token: string;
    }
  | {
      Nonce: string;
      AuthCode: string;
    }
);

interface LoginResult {
  accessToken: string;
}

interface LoginLinkPayload {
  emailAddress: string;
  next?: string;
}

interface LoginLinkResult {
  success: boolean;
  nonce: string;
  expiresAt: string;
}

interface LoginCodePayload {
  token: string;
}

interface LoginCodeResult {
  code: string;
}

interface SignupPayload {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  agreeToTerms: boolean;
  agreeToLoyalty: boolean;
  phoneNumber: string;
  DeviceID: string;
}

interface UpdateProfilePayload {
  emailAddress: string;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
  agreeToLoyalty?: boolean;
  agreeToTerms?: boolean;
}

interface SignupResult {
  accessToken: string;
}

interface SendPasswordResetPayload {
  EmailAddress: string;
}

interface ResetPasswordPayload {
  Password: string;
}

interface VerifyEmailPayload {
  jwt: string;
}

interface SendSupportMessagePayload {
  text: string;
  supportType: string;
}
